import { useCallback, useMemo } from 'react'

import { publishRumEvent, RumEventAttrs } from './helpers/publish-rum-event'

import { SPLUNK_RUM_FIELDS, SplunkRumWorkflows } from '@/constants/splunk'

export type PublishPartialRumEventFn = (attributes: RumEventAttrs) => void

/**
 * Hook to use Splunk RUM (frontend analytics) functionality.
 *
 * @param {SplunkRumWorkflows[keyof SplunkRumWorkflows]} [workflow] - The workflow identifier for the event.
 * @returns An object containing the `publishRumEvent` function, which will send analytics to Splunk RUM dashboard.
 * @example
 * // Without workflow parameter
 * const { publishRumEvent } = useSplunkRum()
 * publishRumEvent({ [SPLUNK_RUM_FIELDS.WORKFLOW]: 'someWorkflow', [SPLUNK_RUM_FIELDS.DHP_PATH]: '/some/path' })
 *
 * // With workflow parameter
 * const { publishRumEvent } = useSplunkRum(SPLUNK_RUM_WORKFLOWS.DHP_ERRORS)
 * publishRumEvent({ [SPLUNK_RUM_FIELDS.DHP_PATH]: '/some/path' })
 *
 * You can find and create new fields and workflows in `src/constants/splunk.ts`.
 */
function useSplunkRum(): { publishRumEvent: typeof publishRumEvent }

function useSplunkRum(workflow: SplunkRumWorkflows[keyof SplunkRumWorkflows]): {
  publishRumEvent: PublishPartialRumEventFn
}
function useSplunkRum(
  workflow?: SplunkRumWorkflows[keyof SplunkRumWorkflows],
):
  | { publishRumEvent: PublishPartialRumEventFn }
  | { publishRumEvent: typeof publishRumEvent } {
  const publishRumEventCallback = useCallback(
    (
      attributes:
        | Parameters<PublishPartialRumEventFn>[0]
        | Parameters<typeof publishRumEvent>[0],
    ) => {
      publishRumEvent({
        [SPLUNK_RUM_FIELDS.WORKFLOW]:
          workflow as SplunkRumWorkflows[keyof SplunkRumWorkflows],
        ...attributes,
      })
    },
    [workflow],
  )

  return useMemo(
    () => ({
      publishRumEvent: publishRumEventCallback,
    }),
    [publishRumEventCallback],
  )
}

export { useSplunkRum }
